import React, { useState } from "react";
import PropTypes from "prop-types";
import { Field, Title, Control, Button } from "rbx";
import { toast } from "react-toastify";
import { useApolloClient, useQuery } from "react-apollo";
import moment from "moment";
import { runPromisesInSeries } from "../../utils";
import TripKeySelect from "../TripKeySelect";
import {
  UPDATE_ORDER_MUTATION,
  ALL_ORDERS_QUERY,
  SINGLE_ORDER_QUERY
} from "../../graphql/Orders/Orders.graphql";
import { useSystemCodes } from "../../utils/hooks";
import BayNumberSelect from "../BayNumberSelect";

class CustomTripKeyError extends Error {}

function TripKeyModal({ orderId, onComplete }) {
  const [tripKey, setTripKey] = useState("");
  const [bayNumber, setBayNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: SystemCodes } = useSystemCodes({
    Category: "Order",
    CodeName: "OrderStatus"
  });
  const client = useApolloClient();

  const { data: OrderData } = useQuery(SINGLE_ORDER_QUERY, {
    variables: { where: { RecId: parseInt(orderId, 10) } }
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const [pickupDate, pickupLocation, trip] = tripKey.split("_");

      const { data: OrdersData } = await client.query({
        query: ALL_ORDERS_QUERY,
        variables: {
          where: {
            Void_not: true,
            Cancel_not: true,
            TripKey_startsWith: `${moment(pickupDate).format(
              "YYYY-MM-DD"
            )}_${pickupLocation}_${trip}`
          }
        },
        fetchPolicy: "network-only"
      });

      const orders = OrdersData?.Orders || [];
      const scheduled = SystemCodes.find(sc => sc.CodeId === "SCH");

      if (orders.some(o => o.StatusRecIdRef !== scheduled.RecId)) {
        throw new CustomTripKeyError(
          "Trip has already been booked. New legs cannot be added."
        );
      }

      // const maxLeg = Math.max(...orders.map(x => x.Leg));

      // const nextLeg = maxLeg + 1;
      const data = {
        Trip: parseInt(trip, 10),
        TripKey: tripKey,
        ComplexTrip: true,
        BayNumber: parseInt(bayNumber, 10)
      };

      if (scheduled) {
        data.StatusRecIdRef = scheduled.RecId;
      }

      await client.mutate({
        mutation: UPDATE_ORDER_MUTATION,
        variables: {
          data,
          where: {
            RecId: parseInt(orderId, 10)
          }
        },
        refetchQueries: [
          {
            query: SINGLE_ORDER_QUERY,
            variables: {
              where: {
                RecId: parseInt(orderId, 10)
              }
            }
          },
          {
            query: ALL_ORDERS_QUERY,
            variables: {
              where: {
                Void_not: true,
                Cancel_not: true,
                TripKey_startsWith: `${moment(pickupDate).format(
                  "YYYY-MM-DD"
                )}_${pickupLocation}_${trip}`
              }
            }
          }
        ]
      });

      await runPromisesInSeries(
        orders.map(order => () =>
          client.mutate({
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              where: {
                RecId: parseInt(order.RecId, 10)
              },
              data: {
                ComplexTrip: true
              }
            }
          })
        )
      );

      toast.success("Order added to Trip successfully.");
      onComplete();
    } catch (err) {
      const message =
        err instanceof CustomTripKeyError
          ? err.message
          : "Whoops! Something went wrong. Please contact support.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const pickupLocationRecIdRef = OrderData?.Order?.PickupLocationRecIdRef || "";

  return (
    <React.Fragment>
      <div className="page-head">
        <div className="page-head-start">
          <Title size={4}>Trip Order</Title>
        </div>
        <div className="page-head-end">
          <Field kind="group">
            <Control>
              <Button disabled={loading} onClick={onComplete}>
                Cancel
              </Button>
            </Control>
            <Control>
              <Button
                color="primary"
                disabled={!tripKey || !bayNumber || loading}
                state={loading ? "loading" : ""}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Control>
          </Field>
        </div>
      </div>
      <hr />
      <Field className="grid-override is-four">
        <TripKeySelect
          disabled={loading}
          name="tripKey"
          value={tripKey}
          onChange={x => setTripKey(x)}
        />
        <BayNumberSelect
          disabled={!tripKey || loading}
          label="Bay Number"
          locationId={pickupLocationRecIdRef}
          name="BayNumber"
          value={bayNumber}
          onChange={(name, value) => setBayNumber(value)}
        />
      </Field>

      <hr />
    </React.Fragment>
  );
}

TripKeyModal.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onComplete: PropTypes.func
};

export default TripKeyModal;
